var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Grid, Typography, styled } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, getUnassignedLOB, renderGroupedOptions } from '../../common';
import { useQueueByIdAPI } from '../queue/selectors';
var StyledContainer = styled(Grid)(function (_a) {
    var theme = _a.theme;
    return ({
        width: '100%',
        maxWidth: theme.typography.pxToRem(700),
    });
});
var NETWORK_LOCATION_ID = -1;
export var ShrinkageCopyFrom = function (_a) {
    var _b;
    var queues = _a.queues, onCopy = _a.onCopy, disabled = _a.disabled;
    var t = useTranslation('wizard').t;
    var _c = useState(null), selectedQueueId = _c[0], setSelectedQueueId = _c[1];
    var _d = useQueueByIdAPI(selectedQueueId), sourceQueueData = _d[0], fetchSourceQueue = _d[1], clearSourceQueueData = _d[2];
    var unassignedLOB = getUnassignedLOB();
    useEffect(function () {
        if (selectedQueueId) {
            fetchSourceQueue();
        }
        else {
            clearSourceQueueData();
        }
    }, [clearSourceQueueData, fetchSourceQueue, selectedQueueId]);
    useEffect(function () {
        var _a;
        if (selectedQueueId && sourceQueueData.data && !sourceQueueData.requesting) {
            var locations = (_a = sourceQueueData.data.queueLocations) !== null && _a !== void 0 ? _a : [];
            if (!locations.length) {
                onCopy({ sourceQueueId: selectedQueueId, sourceQueueLocationId: null });
                setSelectedQueueId(null);
                clearSourceQueueData();
            }
        }
    }, [selectedQueueId, sourceQueueData.data, sourceQueueData.requesting, onCopy, clearSourceQueueData]);
    var locationOptions = useMemo(function () {
        var _a, _b;
        if (!selectedQueueId)
            return [];
        var locations = (_b = (_a = sourceQueueData.data) === null || _a === void 0 ? void 0 : _a.queueLocations) !== null && _b !== void 0 ? _b : [];
        if (locations.length === 0) {
            return [{ id: NETWORK_LOCATION_ID, name: t('shrinkage.networkLevel') }];
        }
        return locations.map(function (loc) { return ({ id: loc.id, name: loc.planLocation.locationName }); });
    }, [selectedQueueId, (_b = sourceQueueData.data) === null || _b === void 0 ? void 0 : _b.queueLocations, t]);
    // The "network level" entry is our own static option, not a queue location.
    var hasQueueLocationOptions = locationOptions.some(function (option) { return option.id !== NETWORK_LOCATION_ID; });
    var _e = useState(null), selectedLocationId = _e[0], setSelectedLocationId = _e[1];
    var handleQueueChange = function (queueId) {
        var id = queueId ? Number(queueId) : null;
        setSelectedQueueId(id);
        setSelectedLocationId(null);
    };
    var handleLocationChange = function (locationId) {
        var id = locationId ? Number(locationId) : null;
        if (!selectedQueueId || id === null) {
            setSelectedLocationId(id);
            return;
        }
        var sourceQueueLocationId = id === NETWORK_LOCATION_ID ? null : id;
        onCopy({ sourceQueueId: selectedQueueId, sourceQueueLocationId: sourceQueueLocationId });
        setSelectedQueueId(null);
        setSelectedLocationId(null);
        clearSourceQueueData();
    };
    return (_jsxs(StyledContainer, __assign({ ml: "auto", container: true, alignItems: "center", columnSpacing: 2 }, { children: [_jsx(Grid, __assign({ item: true, xs: 4, textAlign: "right" }, { children: _jsx(Typography, { children: t('copyData.label') }) })), _jsx(Grid, __assign({ item: true, xs: 4 }, { children: _jsx(Autocomplete, { disabled: disabled, blurOnSelect: true, options: queues, getOptionValue: function (o) { return o.id; }, value: selectedQueueId, placeholder: t('copyData.placeholder'), groupBy: function (option) { var _a; return (_a = option.lobName) !== null && _a !== void 0 ? _a : unassignedLOB; }, renderGroup: renderGroupedOptions, onChange: handleQueueChange, "data-testid": "copyQueueSelect", rumEntity: "queue" }) })), _jsx(Grid, __assign({ item: true, xs: 4, pr: 4 }, { children: _jsx(Autocomplete, { disabled: disabled || !selectedQueueId || !!sourceQueueData.requesting || !locationOptions.length, blurOnSelect: true, options: locationOptions, getOptionValue: function (o) { return o.id; }, value: selectedLocationId, placeholder: t('shrinkage.selectLocation'), onChange: handleLocationChange, "data-testid": "copyLocationSelect", rumEntity: hasQueueLocationOptions ? 'queueLocation' : undefined }) }))] })));
};
